import insightsData from '../components/Insights/insightsData';
import metadataConfig from './routeMetadata.config.json';
import {
  buildCanonical,
  buildDescription,
  buildTitle,
  isLikelyNonProductionHost,
  normalizeRoutePath,
} from './metaRuntime';

const BLOCKED_INDEX_QUERY_PARAMS = new Set(['q', 'query', 'search', 'sort', 'filter', 'filters', 'view']);
const TRACKING_PREFIXES = ['utm_'];
const TRACKING_EXACT = new Set(['gclid', 'fbclid', 'msclkid', 'dclid']);

function compactWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function isTrackingParam(key) {
  const normalized = String(key || '').toLowerCase();
  if (TRACKING_EXACT.has(normalized)) return true;
  return TRACKING_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

function hasBlockedIndexingQuery(search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  for (const [key] of params.entries()) {
    const normalized = String(key || '').toLowerCase();
    if (isTrackingParam(normalized)) continue;
    if (BLOCKED_INDEX_QUERY_PARAMS.has(normalized)) return true;
    if (normalized && normalized !== 'page') return true;
  }
  return false;
}

function buildBaseMeta({
  path,
  pageTitle,
  summary,
  cta,
  intent,
  location,
  indexable = false,
  canonicalPath,
  ogType = 'website',
  host,
}) {
  const brand = metadataConfig.brand || 'Douglas & McCartney';
  const safePath = canonicalPath || path || '/';
  const title = buildTitle({ pageTitle, brand, intent, location });
  const description = buildDescription({ summary, CTA: cta });
  const canonical = buildCanonical(safePath);
  const nonProd = isLikelyNonProductionHost(host);
  const robots = indexable && !nonProd ? 'index,follow' : 'noindex,nofollow';
  const isArticle = ogType === 'article';

  return {
    title,
    description,
    canonical,
    robots,
    indexable: indexable && !nonProd,
    og: {
      'og:title': title,
      'og:description': description,
      'og:url': canonical,
      'og:type': ogType,
      'og:site_name': brand,
      'og:locale': metadataConfig.defaults?.ogLocale || 'en_IN',
    },
    twitter: {
      'twitter:card': metadataConfig.defaults?.twitterCard || 'summary',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:url': canonical,
    },
    article: isArticle ? {} : null,
  };
}

function getStaticRouteMeta(path, host) {
  const entry = metadataConfig.routes?.[path];
  if (!entry) return null;
  return buildBaseMeta({
    path,
    pageTitle: entry.pageTitle,
    summary: entry.summary,
    cta: entry.cta,
    intent: entry.intent || metadataConfig.defaults?.intent,
    location: entry.location || metadataConfig.defaultLocation,
    indexable: Boolean(entry.indexable),
    canonicalPath: entry.canonicalPath || path,
    ogType: entry.ogType || 'website',
    host,
  });
}

function getInsightPostMeta(path, slug, host) {
  const post = insightsData.find((item) => item.slug === slug);
  if (!post) {
    return buildBaseMeta({
      path,
      pageTitle: 'Insight Not Found',
      summary: 'The requested Douglas & McCartney insight article could not be found.',
      cta: 'Browse all insights from Douglas & McCartney.',
      intent: metadataConfig.dynamic?.insightsPost?.intent || 'Broker Insights',
      location: metadataConfig.dynamic?.insightsPost?.location || metadataConfig.defaultLocation,
      indexable: false,
      canonicalPath: '/insights',
      ogType: 'website',
      host,
    });
  }

  const dynamicConfig = metadataConfig.dynamic?.insightsPost || {};
  const description = buildDescription({
    summary: compactWhitespace(post.excerpt || post.title),
    CTA: dynamicConfig.descriptionCTA,
  });

  const base = buildBaseMeta({
    path,
    pageTitle: compactWhitespace(post.title),
    summary: description,
    cta: '',
    intent: dynamicConfig.intent || 'Broker Insights',
    location: dynamicConfig.location || metadataConfig.defaultLocation,
    indexable: true,
    canonicalPath: `/insights/${post.slug}`,
    ogType: 'article',
    host,
  });

  if (base.article) {
    const parsedDate = post.date ? new Date(post.date) : null;
    if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
      base.article['article:published_time'] = parsedDate.toISOString();
    }
    if (post.category) {
      base.article['article:section'] = post.category;
    }
  }

  return base;
}

export function resolveRouteMetadata({ pathname = '/', search = '', hostname = '' } = {}) {
  const path = normalizeRoutePath(pathname);
  const host = String(hostname || '');

  const insightMatch = path.match(/^\/insights\/([^/]+)$/);
  let meta = null;

  if (insightMatch) {
    meta = getInsightPostMeta(path, insightMatch[1], host);
  } else {
    meta = getStaticRouteMeta(path, host);
  }

  if (!meta) {
    meta = buildBaseMeta({
      path,
      pageTitle: 'Douglas & McCartney',
      summary: 'Douglas & McCartney is a broker-focused platform for real estate professionals in India.',
      cta: '',
      intent: metadataConfig.defaults?.intent,
      location: metadataConfig.defaultLocation,
      indexable: false,
      canonicalPath: path,
      ogType: 'website',
      host,
    });
  }

  if (hasBlockedIndexingQuery(search)) {
    meta.robots = 'noindex,nofollow';
    meta.indexable = false;
  }

  return meta;
}

function ensureMetaTag(selector, attributes, content) {
  if (typeof document === 'undefined') return;
  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) return;
  let node = head.querySelector(selector);
  if (!node) {
    node = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    head.appendChild(node);
  }
  if (content) {
    node.setAttribute('content', content);
  } else if (node.parentNode) {
    node.parentNode.removeChild(node);
  }
}

function ensureLinkTag(selector, attributes, href) {
  if (typeof document === 'undefined') return;
  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) return;
  let node = head.querySelector(selector);
  if (!node) {
    node = document.createElement('link');
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    head.appendChild(node);
  }
  if (href) {
    node.setAttribute('href', href);
  } else if (node.parentNode) {
    node.parentNode.removeChild(node);
  }
}

export function applyDocumentMetadata(meta) {
  if (typeof document === 'undefined' || !meta) return;

  if (meta.title) {
    document.title = meta.title;
  }

  ensureMetaTag('meta[name="description"]', { name: 'description' }, meta.description || '');
  ensureMetaTag('meta[name="robots"]', { name: 'robots' }, meta.robots || '');
  ensureLinkTag('link[rel="canonical"]', { rel: 'canonical' }, meta.canonical || '');

  Object.entries(meta.og || {}).forEach(([property, value]) => {
    ensureMetaTag(`meta[property="${property}"]`, { property }, value || '');
  });

  Object.entries(meta.twitter || {}).forEach(([name, value]) => {
    ensureMetaTag(`meta[name="${name}"]`, { name }, value || '');
  });

  const articleProperties = ['article:published_time', 'article:section'];
  if (meta.article) {
    const articleEntries = Object.entries(meta.article).filter(([, value]) => value);
    articleEntries.forEach(([property, value]) => {
      ensureMetaTag(`meta[property="${property}"]`, { property }, value);
    });
    for (const property of articleProperties) {
      if (!Object.prototype.hasOwnProperty.call(meta.article, property) || !meta.article[property]) {
        ensureMetaTag(`meta[property="${property}"]`, { property }, '');
      }
    }
  } else {
    for (const property of articleProperties) {
      ensureMetaTag(`meta[property="${property}"]`, { property }, '');
    }
  }
}
